import React, { useState } from 'react';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, Anchor, ShieldAlert, Video } from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
  {
    id: 'Level 1',
    lashes: 25,
    subtitle: 'Introduction',
    text: 'A meaningful strapping for those ready to feel the strap properly for the first time.',
  },
  {
    id: 'Level 2',
    lashes: 50,
    subtitle: 'Standard',
    text: 'A serious punishment strapping — twice as many lashes as Level 1. For those who need real accountability.',
  },
  {
    id: 'Level 3',
    lashes: 100,
    subtitle: 'Severe',
    text: 'The most severe strapping I offer. Reserved for those who genuinely need or want a maximum-consequence session.',
  },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const WEEKDAY_SLOTS = ['8:00 PM – 9:00 PM'];

const SATURDAY_SLOTS = [
  '9:00 AM – 10:00 AM',
  '10:00 AM – 11:00 AM',
  '11:00 AM – 12:00 PM',
  '12:00 PM – 1:00 PM',
  '1:00 PM – 2:00 PM',
  '2:00 PM – 3:00 PM',
  '3:00 PM – 4:00 PM',
  '4:00 PM – 5:00 PM',
  '5:00 PM – 6:00 PM',
  '6:00 PM – 7:00 PM',
  '7:00 PM – 8:00 PM',
  '8:00 PM – 9:00 PM',
];

const getTimeSlotsForDay = (day) => {
  if (day === 'Saturday') return SATURDAY_SLOTS;
  if (day) return WEEKDAY_SLOTS;
  return [];
};

const initialForm = {
  firstName: '',
  location: '',
  email: '',
  gender: '',
  level: '',
  preferredDay: '',
  preferredTime: '',
  notes: '',
  waiver: false,
  adult: '',
};

const InfoCard = ({ icon: Icon, title, text }) => (
  <div className="bg-white border border-neutral-200 p-6 text-center">
    <Icon size={28} className="mx-auto text-neutral-900 mb-3" strokeWidth={1.5} />
    <h3 className="font-serif text-lg text-neutral-900 mb-1">{title}</h3>
    <p className="text-neutral-600 text-sm leading-relaxed">{text}</p>
  </div>
);

const RealStrapping = () => {
  usePageMeta({
    title: 'Real Strapping — Punishment Level Discipline',
    description: 'Book a real strapping session with RealOTK. Three levels (25, 50, or 100 lashes) administered on the bare bottom while restrained to a spanking bench. The only disciplinary session offered to males (waiver required).',
    path: '/real-strapping',
  });

  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isMale = form.gender === 'Male';

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.adult !== 'yes') {
      toast({ title: 'Age verification required', description: 'You must confirm you are 18 or older.' });
      return;
    }
    if (!form.email || !form.gender || !form.level) {
      toast({ title: 'Missing information', description: 'Please fill in email, gender, and level.' });
      return;
    }
    if (!form.preferredDay || !form.preferredTime) {
      toast({ title: 'Pick a time slot', description: 'Please choose a day and a time slot.' });
      return;
    }
    if (isMale && !form.waiver) {
      toast({
        title: 'Waiver required',
        description: 'Male clients must agree to the recording waiver to book a Real Strapping session.',
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/real-strapping-request`, {
        first_name: form.firstName || null,
        location: form.location || null,
        email: form.email.trim(),
        gender: form.gender,
        level: form.level,
        preferred_day: form.preferredDay || null,
        preferred_time: form.preferredTime || null,
        waiver_agreed: !!form.waiver,
        notes: form.notes.trim() || null,
        adult: form.adult,
      });
      setSubmitted(true);
      trackEvent('real_strapping_request', { level: form.level });
      toast({ title: 'Request received', description: 'I will follow up privately to confirm.' });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Bench discipline"
        title="Real Strapping"
        subtitle="Restrained to a spanking bench. Bare bottom from the first lash. No half-measures."
        image="https://images.unsplash.com/photo-1621365039903-fd09cc003898?auto=format&fit=crop&w=3840"
      />

      {/* Intro */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 md:py-24">
          <p className="text-neutral-500 text-xs uppercase tracking-[0.35em] mb-6 text-center">How it works</p>
          <h2 className="font-serif text-3xl md:text-5xl text-neutral-900 font-bold tracking-tight leading-[1.15] text-center">
            Punishment level, from the first stroke
          </h2>
          <div className="mt-8 mx-auto w-16 h-px bg-neutral-900" />
          <p className="mt-10 text-lg text-neutral-700 leading-relaxed">
            All Real Strapping sessions require the person to be <strong>restrained to a spanking bench</strong>{' '}
            and are administered at a <strong>punishment level</strong>, meaning the strap begins across the
            <strong> bare bottom starting with the first lash</strong>. There is no warm-up. Once your punishment
            starts, you will receive every stroke until the end. Choose the level appropriate for you.
          </p>
        </div>
      </section>

      {/* Levels */}
      <section className="bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid md:grid-cols-3 gap-6">
            {LEVELS.map((lvl) => (
              <div
                key={lvl.id}
                className={`bg-white border p-8 flex flex-col transition-all cursor-pointer ${
                  form.level === lvl.id
                    ? 'border-neutral-900 shadow-md -translate-y-1'
                    : 'border-neutral-200 hover:border-neutral-400 hover:-translate-y-0.5'
                }`}
                onClick={() => setForm({ ...form, level: lvl.id })}
              >
                <p className="text-neutral-500 text-xs uppercase tracking-[0.3em] mb-3">{lvl.subtitle}</p>
                <h3 className="font-serif text-3xl md:text-4xl text-neutral-900 font-bold mb-2">{lvl.id}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="font-serif text-5xl text-neutral-900 font-bold">{lvl.lashes}</span>
                  <span className="text-neutral-600 text-sm">lashes with the strap</span>
                </div>
                <p className="text-neutral-700 text-sm leading-relaxed flex-1">{lvl.text}</p>
                <div
                  className={`mt-6 text-center py-2 text-xs uppercase tracking-widest border ${
                    form.level === lvl.id
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'border-neutral-300 text-neutral-700'
                  }`}
                >
                  {form.level === lvl.id ? 'Selected' : 'Select this level'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info tiles */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-4">
          <InfoCard
            icon={Anchor}
            title="Restrained to the bench"
            text="You will be firmly secured to the spanking bench so you see the punishment through from start to finish."
          />
          <InfoCard
            icon={ShieldAlert}
            title="Bare bottom, no warm-up"
            text="Every stroke lands on the bare bottom starting with the first lash. Once it begins, it continues to the end."
          />
          <InfoCard
            icon={Video}
            title="Males allowed with waiver"
            text="This is the only disciplinary session I offer to males. Session must be recorded. No face shots. Waiver required."
          />
        </div>
      </section>

      {/* Form */}
      <section className="bg-neutral-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 md:py-24">
          <div className="text-center mb-10">
            <p className="text-neutral-500 text-xs uppercase tracking-[0.35em] mb-4">Schedule</p>
            <h2 className="font-serif text-3xl md:text-4xl text-neutral-900 font-bold">
              Book Your Real Strapping
            </h2>
            <p className="text-neutral-600 mt-4 max-w-xl mx-auto">
              Pick a day and time slot below and choose the level you deserve. Sessions run weekdays{' '}
              <strong>8&ndash;9 PM</strong>, or any 1-hour block on <strong>Saturday, 9 AM&ndash;9 PM</strong>.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white border border-neutral-200 p-10 md:p-14 text-center">
              <CheckCircle2 size={48} className="mx-auto text-neutral-900 mb-4" />
              <h3 className="font-serif text-3xl text-neutral-900 mb-3">Request Received</h3>
              <p className="text-neutral-600 max-w-md mx-auto">
                Thank you. I will follow up privately with a confirmation, exact time, address, and
                {isMale ? ' the recording waiver documentation.' : ' any final questions.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 p-8 md:p-12 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="strap-firstName" className="text-xs uppercase tracking-widest text-neutral-700">First Name</Label>
                  <Input id="strap-firstName" value={form.firstName} onChange={handleChange('firstName')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
                </div>
                <div>
                  <Label htmlFor="strap-location" className="text-xs uppercase tracking-widest text-neutral-700">Location</Label>
                  <Input id="strap-location" value={form.location} onChange={handleChange('location')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
                </div>
              </div>

              <div>
                <Label htmlFor="strap-email" className="text-xs uppercase tracking-widest text-neutral-700">Your Email *</Label>
                <Input id="strap-email" type="email" required value={form.email} onChange={handleChange('email')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-700">Gender *</Label>
                <RadioGroup value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })} className="flex flex-wrap gap-6 mt-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Female" id="strap-g-f" />
                    <Label htmlFor="strap-g-f" className="cursor-pointer">Female</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Male" id="strap-g-m" />
                    <Label htmlFor="strap-g-m" className="cursor-pointer">Male</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-700">Level *</Label>
                  <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                    <SelectTrigger className="mt-2 rounded-none border-neutral-300 h-11">
                      <SelectValue placeholder="Select a level..." />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.id} — {l.lashes} lashes</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-700">Preferred Day *</Label>
                  <Select
                    value={form.preferredDay}
                    onValueChange={(v) => setForm({ ...form, preferredDay: v, preferredTime: '' })}
                  >
                    <SelectTrigger className="mt-2 rounded-none border-neutral-300 h-11">
                      <SelectValue placeholder="Select a day..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-700">Preferred Time *</Label>
                <Select
                  value={form.preferredTime}
                  onValueChange={(v) => setForm({ ...form, preferredTime: v })}
                  disabled={!form.preferredDay}
                >
                  <SelectTrigger className="mt-2 rounded-none border-neutral-300 h-11 disabled:opacity-60">
                    <SelectValue placeholder={form.preferredDay ? 'Select a time...' : 'Choose a day first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {getTimeSlotsForDay(form.preferredDay).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.preferredDay && form.preferredDay !== 'Saturday' && (
                  <p className="text-xs text-neutral-500 mt-2">
                    Weekday sessions are only available in the 8:00 PM – 9:00 PM slot.
                  </p>
                )}
                {form.preferredDay === 'Saturday' && (
                  <p className="text-xs text-neutral-500 mt-2">
                    Saturday: any 1-hour block between 9:00 AM and 9:00 PM.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="strap-notes" className="text-xs uppercase tracking-widest text-neutral-700">Notes (optional)</Label>
                <Textarea id="strap-notes" rows={4} value={form.notes} onChange={handleChange('notes')} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 resize-none" placeholder="Anything I should know before your session..." />
              </div>

              {isMale && (
                <div className="bg-red-50 border border-red-200 p-5">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="strap-waiver"
                      checked={!!form.waiver}
                      onCheckedChange={(v) => setForm({ ...form, waiver: !!v })}
                      className="mt-1"
                    />
                    <Label htmlFor="strap-waiver" className="text-sm text-neutral-800 leading-relaxed cursor-pointer">
                      <span className="font-semibold text-red-900 block mb-1">Recording waiver — required for male clients</span>
                      I understand and agree that my Real Strapping session will be recorded on video.
                      No face shots will be taken. A signed waiver will be provided in person before the session begins.
                    </Label>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-700">Are you 18+?</Label>
                <RadioGroup value={form.adult} onValueChange={(v) => setForm({ ...form, adult: v })} className="flex gap-6 mt-3">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="strap-a-y" />
                    <Label htmlFor="strap-a-y" className="cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="strap-a-n" />
                    <Label htmlFor="strap-a-n" className="cursor-pointer">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-12 text-sm tracking-widest uppercase disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Submit Real Strapping Request'}
              </Button>
              <p className="text-xs text-neutral-500 text-center">
                Your information stays private. I will follow up personally within 24-48 hours.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default RealStrapping;
